import { SpotCheckbox, SpotCheckboxGroup, SpotHorizontalFields } from '@enterprise/spot';
import { LookupDiscount, LookupTax } from '../../../../../core/models/datasource/lookups';
import { Trans } from 'react-i18next';
import { observer } from 'mobx-react';
import { useStores } from '../../../../../hooks';
import { useField, useFormState } from '@enterprise/common';
import { InvoiceItemTax, InvoiceItemTypes, InvoiceItemWithVariantDetail } from '../../../../../core/models';
import { GroupSelectField } from '../../../components';

export const TaxDiscControl = observer(function TaxDiscControls() {
    const {
        domain: { itemsPageStore },
    } = useStores();

    const discounts = itemsPageStore.getLookupResults(itemsPageStore.discounts);
    const taxes = itemsPageStore.getLookupResults(itemsPageStore.taxes);
    const { values } = useFormState<InvoiceItemWithVariantDetail>();
    const { itemType } = values;

    const taxesField = useField<InvoiceItemTax[]>('taxes');

    const onTaxesChange = (option: LookupTax, checked: boolean, isUsageTax: boolean) => {
        const taxes = taxesField.input.value;

        if (checked) {
            taxesField.input.onChange([...taxes, { ...option, id: String(option.id), isUsageTax }]);
        } else {
            const newTaxes = taxes.filter((item) => item.id !== String(option.id) || item.isUsageTax !== isUsageTax);
            taxesField.input.onChange(newTaxes);
        }
    };

    const checked = (option: LookupTax, isUsageTax: boolean): boolean => {
        if (!taxesField.input.value) {
            return false;
        }

        const selected = taxesField.input.value.find((item) => item.id === String(option.id) && item.isUsageTax === isUsageTax);
        return Boolean(selected);
    };

    return (
        <div>
            <SpotHorizontalFields>
                <div data-automation-id="sales-tax-container">
                    <label className="spot-form__label">
                        <Trans i18nKey="controlCenter:item.salesTaxToApply">Sales tax to apply</Trans>
                    </label>
                    <SpotCheckboxGroup isHorizontal={true}>
                        {taxes.map((option) => (
                            <div key={String(option.id)}>
                                <SpotCheckbox
                                    type="checkbox"
                                    disabled={itemType?.id === InvoiceItemTypes.Service}
                                    checked={checked(option, false)}
                                    onChange={(event) => onTaxesChange(option, event.target.checked, false)}
                                    data-automation-id={'tax-discount-controls-sales-tax'}
                                >
                                    {option.description}
                                </SpotCheckbox>
                            </div>
                        ))}
                    </SpotCheckboxGroup>
                </div>
                <div data-automation-id="usage-tax-container">
                    <label className="spot-form__label">
                        <Trans i18nKey="controlCenter:item.usageTaxToApply">Usage tax to apply</Trans>
                    </label>
                    <SpotCheckboxGroup isHorizontal={true}>
                        {taxes.map((option) => (
                            <div key={String(option.id)}>
                                <SpotCheckbox
                                    type="checkbox"
                                    disabled={itemType?.id === InvoiceItemTypes.Service}
                                    checked={checked(option, true)}
                                    onChange={(event) => onTaxesChange(option, event.target.checked, true)}
                                    data-automation-id={'tax-discount-controls-usage-tax'}
                                >
                                    {option.description}
                                </SpotCheckbox>
                            </div>
                        ))}
                    </SpotCheckboxGroup>
                </div>
            </SpotHorizontalFields>
            <div data-automation-id="discounts-container">
                <label className="spot-form__label">
                    <Trans i18nKey="controlCenter:item.discountsToApply">Discounts to apply</Trans>
                </label>
                <SpotCheckboxGroup>
                    <GroupSelectField
                        name="discounts"
                        options={discounts}
                        getOptionId={({ id }: LookupDiscount) => String(id)}
                        getOptionTitle={({ description }: LookupDiscount) => description}
                        dataAutomationId={'tax-discount-controls-discounts'}
                    />
                </SpotCheckboxGroup>
            </div>
        </div>
    );
});
